import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import Input from "components/Input";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import Button from "components/Button";
import CopyIcon from "assets/icons/copy.svg?react";
import { useAuth } from "react-oidc-context";
import activationService from "api/activationService";
import classNames from "classnames";
import Spinner from "components/Spinner";
import { getErrorMessage } from "utils/getErrorMessage";
import { IMobywatelQRCodeResponse } from "dto/Activation/IMobywatelQRCodeResponse";
import { ENotificationType, notification } from "utils/notification";
import { QRCodeCanvas } from "qrcode.react";
import { SignalRContext } from "./../../../App";
import { HubCallbacksNames, IUserIdentified } from "api/hub";
import { getUserClaim } from "utils/getUserClaim";
import { Claims } from "dto/User/claims";
import { Tooltip } from "@mui/material";
import { useMediaQuery } from "usehooks-ts";

// Jedyna metoda weryfikacji tożsamości to mObywatel (ścieżka z podpisanym
// oświadczeniem została usunięta razem z doreczeniaelektroniczne.pl).
export enum IdentityStep {
  MObywatelQRCode = "mobywatelQRCode",
  Success = "success",
}

const Identity: FC<Props> = ({ nextStep }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const isMobile = useMediaQuery("(max-width: 767px)");

  SignalRContext.useSignalREffect(
    HubCallbacksNames.UserIdentified,
    (message: IUserIdentified) => {
      if (message.success) {
        auth.revokeTokens();
        auth.signinSilent();
        setStep(IdentityStep.Success);
      } else {
        notification({
          title: t("identity.verificationFailed"),
          text: message.message,
          type: ENotificationType.ERROR,
        });
      }
    },
    [],
  );

  const [personalData, setPersonalData] = useState<{
    firstName: string;
    lastName: Nullable<string>;
    pesel: Nullable<string>;
  }>({
    firstName: getUserClaim(auth.user!, Claims.Eidas_FirstName),
    lastName: getUserClaim(auth.user!, Claims.Eidas_FamilyName),
    pesel: getUserClaim(auth.user!, Claims.Eidas_PersonIdentifier),
  });

  const [step, setStep] = useState<IdentityStep>(IdentityStep.MObywatelQRCode);
  const [qrCodeData, setQRCodeData] = useState<string>("");
  const [key, setKey] = useState<string>("");

  const { mutate: getMobywatelQRCode, isLoading: isLoadingQRCode } =
    activationService.useGetMObywatelQRcode();

  const initMObywatelQRCode = () => {
    getMobywatelQRCode(
      {},
      {
        onSuccess(data) {
          const resp = data.data as IMobywatelQRCodeResponse;
          setQRCodeData(resp.Raw);
          setKey(resp.Raw);
        },
        onError(e: any) {
          notification({
            title: t("errors.somethingWentWrong"),
            text: getErrorMessage(e.response.data),
            type: ENotificationType.ERROR,
          });
        },
      },
    );
  };

  const handleOpenmObywatel = () => {
    if (key.length) {
      window.open(`mobywatel://app/institutions?qrCode=${key}`, "_blank");
    }
  };

  useEffect(() => {
    initMObywatelQRCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (auth.user) {
      setPersonalData((prevState) => ({
        ...prevState,
        firstName: getUserClaim(auth.user!, Claims.Eidas_FirstName),
        lastName: getUserClaim(auth.user!, Claims.Eidas_FamilyName),
        pesel: getUserClaim(auth.user!, Claims.Eidas_PersonIdentifier),
      }));
    }
  }, [auth.user]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>{t("identity.verifyTitle")}</div>

      {step === IdentityStep.MObywatelQRCode && (
        <>
          <div className={styles.subheader}>{t("identity.verifyMessage")}</div>
          <div className={styles.hint}>
            {!isMobile && <b>{t("identity.scanQRCode")}</b>}
            {isMobile && <b>{t("identity.verifyIdentityTitle")}</b>}
            <br></br>
            {!isMobile && <>{t("identity.mobywatelDesktop")}</>}
            {isMobile && <>{t("identity.mobywatelMobile")}</>}
            <br></br>
            {t("identity.requiredData")}
          </div>
          {!isMobile && qrCodeData && (
            <div className={styles.qrcode}>
              <QRCodeCanvas value={qrCodeData} size={256} />
            </div>
          )}
          {isMobile && !isLoadingQRCode && (
            <div className={styles.center}>
              <div className={styles.applink}>
                <Button
                  text={t("identity.goToMobywatel")}
                  onClick={handleOpenmObywatel}
                ></Button>
              </div>
              <div className={styles.or}>{t("identity.copyCode")}</div>
              <div className={styles.keyBox}>
                <div className={styles.key}>{key}</div>
                <div
                  className={styles.copy}
                  onClick={() => navigator.clipboard.writeText(key)}
                >
                  <Tooltip title={t("common.copy")}>
                    <div>
                      <CopyIcon></CopyIcon>
                    </div>
                  </Tooltip>
                </div>
              </div>
            </div>
          )}

          {isLoadingQRCode && (
            <div className={styles.loading}>
              <Spinner className={styles.spinner} />
              <div>{t("common.loading")}</div>
            </div>
          )}
        </>
      )}

      {step === IdentityStep.Success && (
        <>
          <div className={styles.subheader}>
            {t("identity.verificationSuccess")}
          </div>
          <div className={styles.dataRow}>
            <Input
              disabled={true}
              value={personalData.firstName}
              RightIcon={
                <div
                  className={classNames(styles.verification, styles.verified)}
                >
                  {t("identity.verifiedData")}
                </div>
              }
              label={t("forms.firstName")}
              onChange={() => {}}
            ></Input>
          </div>
          <div className={styles.dataRow}>
            <Input
              disabled={true}
              value={personalData.lastName}
              RightIcon={
                <div
                  className={classNames(styles.verification, styles.verified)}
                >
                  {t("identity.verifiedData")}
                </div>
              }
              label={t("forms.lastName")}
              onChange={() => {}}
            ></Input>
          </div>
          <div className={styles.dataRow}>
            <Input
              disabled={true}
              value={personalData.pesel}
              RightIcon={
                <div
                  className={classNames(styles.verification, styles.verified)}
                >
                  {t("identity.verifiedData")}
                </div>
              }
              label={t("forms.pesel")}
              onChange={() => {}}
            ></Input>
          </div>

          <div className={styles.buttons}>
            <Button
              className={styles.button}
              text={t("common.next")}
              onClick={() => nextStep?.()}
            ></Button>
          </div>
        </>
      )}
    </div>
  );
};

export default Identity;
